import { MultipleQueriesQuery, MultipleQueriesResponse, SearchOptions, SearchResponse } from '@algolia/client-search'
import algoliasearch, { SearchIndex } from 'algoliasearch/lite'
import { default as algoliaSearchInsights } from 'search-insights'

import {
  ALGOLIA_INDEX_PRODUCT,
  ALGOLIA_INDEX_PRODUCT_PRICE_ASC,
  ALGOLIA_INDEX_PRODUCT_PRICE_DESC,
  ALGOLIA_INDEX_PRODUCT_PUBLISHED_ASC,
  ALGOLIA_INDEX_PRODUCT_PUBLISHED_DESC,
} from '@src/config/env'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import { AlgoliaSearchResult } from '@src/quince-site-common/src/services/algoliaService/algolia.types'
import { SUPPORTED_FACETS } from '@src/quince-site-common/src/utils/searchFilter/searchFilterConstants'
import { AlgoliaAutoSuggestResult } from '@src/SectionRenderer/sections/searchBoxSection/searchBox/SearchBox'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'

import { getLbUserIdWithoutColons } from '../../utils/getlb-user-id'

import { getReplaceableQuery } from './algoliaReplaceQueries'
import { isSearchResult } from './utils'

export const ALGOLIA_INDEX_SUGGESTION = 'quince_product_color_index_query_suggestions'
// @i18n-ignore
const ALGOLIA_APP_ID = 'K81D2WQH9L'
// @i18n-ignore
const ALGOLIA_SEARCH_KEY = '3f306f13f22cfa9128f18019ba17dc34'

export const ALGOLIA_CACHE_TIMEOUT = 60 * 60 * 1000

export type SearchResultsType = SearchResponse<AlgoliaSearchResult | AlgoliaAutoSuggestResult>
export type AlgoliaSearchResponse = MultipleQueriesResponse<SearchResultsType>
export type AlgoliaSearchWithFacetResponse = SearchResponse<SearchResultsType>

export interface AlgoliaBuildTimeResponse {
  trendingSearchSuggestions: SearchResponse<SearchResultsType>
}

interface TAlgoliaAddedToCartEvent {
  objectID: string
  queryID: string
}

interface TAlgoliaTrackClick {
  objectID?: string
  position?: number
  queryID?: string
}

export type INDEX_TYPES = typeof ALGOLIA_INDEX_PRODUCT
export const algoliaSearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
export const algoliaProductSearchIndex = algoliaSearchClient.initIndex(ALGOLIA_INDEX_PRODUCT)
export const algoliaProductPriceAscSearchIndex = algoliaSearchClient.initIndex(ALGOLIA_INDEX_PRODUCT_PRICE_ASC)
export const algoliaProductPriceDescSearchIndex = algoliaSearchClient.initIndex(ALGOLIA_INDEX_PRODUCT_PRICE_DESC)
export const algoliaProductPublishedDescSearchIndex = algoliaSearchClient.initIndex(
  ALGOLIA_INDEX_PRODUCT_PUBLISHED_DESC,
)
export const algoliaProductPublishedAscSearchIndex = algoliaSearchClient.initIndex(ALGOLIA_INDEX_PRODUCT_PUBLISHED_ASC)

const ALGOLIA_INDEX_NAME_TO_SEARCH_INDEX: Record<string, SearchIndex> = {
  [ALGOLIA_INDEX_PRODUCT]: algoliaProductSearchIndex,
  [ALGOLIA_INDEX_PRODUCT_PRICE_ASC]: algoliaProductPriceAscSearchIndex,
  [ALGOLIA_INDEX_PRODUCT_PRICE_DESC]: algoliaProductPriceDescSearchIndex,
  [ALGOLIA_INDEX_PRODUCT_PUBLISHED_ASC]: algoliaProductPublishedAscSearchIndex,
  [ALGOLIA_INDEX_PRODUCT_PUBLISHED_DESC]: algoliaProductPublishedDescSearchIndex,
}

export const SORT_BY_TO_INDEX_NAME: Record<string, INDEX_TYPES> = {
  BEST_SELLER: ALGOLIA_INDEX_PRODUCT,
  DEFAULT: ALGOLIA_INDEX_PRODUCT,
  [SORT_BY.PRICE_LOW_TO_HIGH.value]: ALGOLIA_INDEX_PRODUCT_PRICE_ASC,
  [SORT_BY.PRICE_HIGH_TO_LOW.value]: ALGOLIA_INDEX_PRODUCT_PRICE_DESC,
  [SORT_BY.OLDEST_TO_NEWEST.value]: ALGOLIA_INDEX_PRODUCT_PUBLISHED_DESC,
  [SORT_BY.NEWEST_TO_OLDEST.value]: ALGOLIA_INDEX_PRODUCT_PUBLISHED_ASC,
}

algoliaSearchInsights('init', {
  appId: ALGOLIA_APP_ID,
  apiKey: ALGOLIA_SEARCH_KEY,
})

export const getSortByIndex = (sortBy = '') => SORT_BY_TO_INDEX_NAME[sortBy] ?? SORT_BY_TO_INDEX_NAME.DEFAULT

export async function algoliaSearch(
  query: string,
  hitsPerPage: number,
  indexName: typeof ALGOLIA_INDEX_SUGGESTION,
  page = 0,
): Promise<AlgoliaSearchResponse> {
  const queries = [
    {
      indexName,
      query: getReplaceableQuery(query),
      params: {
        hitsPerPage,
        clickAnalytics: true,
        page,
      },
    },
  ]
  return algoliaSearchQueries(queries)
}

export async function algoliaSearchFacets(query: string, params: SearchOptions, indexName: string) {
  const algoliaIndex = ALGOLIA_INDEX_NAME_TO_SEARCH_INDEX[indexName]
  const response = await algoliaIndex?.search(query, params)
  return response
}

export async function algoliaSearchWithFacet(
  query: string,
  hitsPerPage: number,
  indexName: string,
  page = 0,
  _params: {
    facetFilters: string[][]
    numericFilters: string[]
  },
) {
  const { facetFilters, numericFilters } = _params
  const params = {
    hitsPerPage,
    page,
    facets: Array.from(SUPPORTED_FACETS),
    facetFilters,
    numericFilters,
    clickAnalytics: true,
    userToken: getLbUserIdWithoutColons(),
  }
  return algoliaSearchFacets(query, params, indexName)
}

export async function algoliaSearchQueries(queries: readonly MultipleQueriesQuery[]) {
  const res = await algoliaSearchClient.search<SearchResultsType>(queries)
  return res
}

export async function algoliaSearchBuildTime(): Promise<AlgoliaBuildTimeResponse> {
  const res = await algoliaSearchClient.search<SearchResultsType>([
    {
      indexName: ALGOLIA_INDEX_SUGGESTION,
      query: '',
      params: { hitsPerPage: 5 },
    },
  ])
  const [trendingSearchSuggestions] = res.results
  return {
    trendingSearchSuggestions: trendingSearchSuggestions as unknown as SearchResponse<SearchResultsType>,
  }
}

export function trackClickForSearchResultsProduct({ objectID, position, queryID }: TAlgoliaTrackClick) {
  void algoliaSearchInsights('clickedObjectIDsAfterSearch', {
    userToken: getLbUserIdWithoutColons(),
    eventName: TELEMETRY_EVENT_NAMES.PRODUCT_CLICK,
    index: ALGOLIA_INDEX_PRODUCT,
    queryID: queryID ?? '',
    objectIDs: objectID ? [objectID] : [],
    positions: position ? [position] : [],
  })
}

export function trackClickObjectIds({ objectID }: TAlgoliaTrackClick) {
  void algoliaSearchInsights('clickedObjectIDs', {
    userToken: getLbUserIdWithoutColons(),
    eventName: TELEMETRY_EVENT_NAMES.PRODUCT_CLICK,
    index: ALGOLIA_INDEX_PRODUCT,
    objectIDs: objectID ? [objectID] : [],
  })
}

export function trackPageViewForProduct({ objectID }: { objectID?: string }) {
  void algoliaSearchInsights('viewedObjectIDs', {
    userToken: getLbUserIdWithoutColons(),
    index: ALGOLIA_INDEX_PRODUCT,
    eventName: TELEMETRY_EVENT_NAMES.PRODUCT_VIEW,
    objectIDs: objectID ? [objectID] : [],
  })
}

export function trackAddToCartForSearchResultsProduct({ objectID, queryID }: TAlgoliaAddedToCartEvent) {
  void algoliaSearchInsights('addedToCartObjectIDsAfterSearch', {
    userToken: getLbUserIdWithoutColons(),
    index: ALGOLIA_INDEX_PRODUCT,
    eventName: TELEMETRY_EVENT_NAMES.ADD_TO_CART,
    queryID: queryID,
    objectIDs: [objectID],
  })
}

export function trackAddedToCartObjectIDs({ objectID }: { objectID: string }) {
  void algoliaSearchInsights('addedToCartObjectIDs', {
    userToken: getLbUserIdWithoutColons(),
    index: ALGOLIA_INDEX_PRODUCT,
    eventName: TELEMETRY_EVENT_NAMES.ADD_TO_CART,
    objectIDs: [objectID],
  })
}

export const handleTrackAddedToCartAlgolia = ({ objectID, queryID }: TAlgoliaAddedToCartEvent) => {
  if (isSearchResult()) trackAddToCartForSearchResultsProduct({ objectID, queryID })
  else trackAddedToCartObjectIDs({ objectID })
}

export const handleAlgoliaClickEvent = ({ objectID, position, queryID }: TAlgoliaTrackClick) => {
  if (isSearchResult()) trackClickForSearchResultsProduct({ objectID, position, queryID })
  else trackClickObjectIds({ objectID })
}
